import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, Star, Building2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function OwnerSurvey() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/owner-survey/${token}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setData(d); if (d.submitted) setDone(true); })
      .catch(() => setErr(true));
  }, [token]);

  const submit = async () => {
    if (rating < 1) { alert("Please select a rating / 请选择评分"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/owner-survey/${token}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment }),
      });
      if (!r.ok) throw new Error();
      setDone(true);
    } catch { alert("Submit failed, please retry / 提交失败请重试"); }
    setBusy(false);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm" data-testid="owner-survey-invalid">Invalid or expired link / 链接无效</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="owner-survey-page">
      <div className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-slate-900 px-6 py-5">
          <div className="flex items-center gap-2 text-white">
            <Building2 className="h-5 w-5 text-red-400" />
            <span className="font-bold">ALIGCUS Construction · 阿里建筑</span>
          </div>
          <p className="text-slate-300 text-xs mt-1">Project Satisfaction Survey / 项目满意度评价</p>
        </div>
        {done ? (
          <div className="p-10 text-center" data-testid="owner-survey-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-semibold text-slate-800">Thank you! / 感谢您的评价</p>
            <p className="text-sm text-slate-400 mt-1">Your feedback helps us build better. / 您的反馈将帮助我们做得更好。</p>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <div>
              <p className="text-sm font-semibold text-slate-800" data-testid="owner-survey-project">{data.clientName}{data.address ? ` · ${data.address}` : ""}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">How satisfied are you with your completed project? / 您对完工项目的整体满意度？</p>
            </div>
            <div className="flex items-center justify-center gap-2" data-testid="owner-survey-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`owner-survey-star-${n}`} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)}>
                  <Star className={`h-9 w-9 transition-colors ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                </button>
              ))}
            </div>
            <p className="text-center text-xs font-semibold text-slate-500 h-4" data-testid="owner-survey-rating-label">
              {rating > 0 && ["", "Poor / 不满意", "Fair / 一般", "Good / 满意", "Very good / 很满意", "Excellent / 非常满意"][rating]}
            </p>
            <textarea data-testid="owner-survey-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
              placeholder="Any comments? (optional) / 想对我们说的话（选填）"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
            <button data-testid="owner-survey-submit" disabled={busy} onClick={submit}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg py-3 text-sm disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Submit / 提交评价
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
